// The three feedback sections for one submitted TARA writing task.
//
// The only component here that fetches. Everything below it takes props, which
// is what makes any of it testable: nothing in this repository can render
// TaraWorkspace under Jest.
//
// Sections stack and stay visible. Whatever a student unlocked before is
// rebuilt from the database on the next visit, in one request, with no model
// calls.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import {
  TARA_FEEDBACK_STAGES, TARA_STAGE_TITLES, taraFeedbackGating,
} from '../utils/taraFeedbackGating';
import { taraFeedbackFromAttempt } from '../utils/taraFeedbackFromAttempt';
import { taraFeedbackErrorCopy } from '../utils/taraFeedbackErrorCopy';
import { taraFeedbackProgressCopy } from '../utils/taraFeedbackProgressCopy';
import TaraFeedbackSection from './TaraFeedbackSection';
import TaraDiagnosticFeedback from './TaraDiagnosticFeedback';
import TaraEnrichmentFeedback from './TaraEnrichmentFeedback';
import TaraPerformanceReport from './TaraPerformanceReport';
import './TaraWritingFeedbackPanel.css';
import {
  TARA_FEEDBACK_IN_FLIGHT_KEY,
  readUserSession,
  removeUserSession,
  writeUserSession,
} from '../utils/paperProgressCache';
import { useAuth } from '../../auth/hooks/useAuth';

const RUN_LABELS = {
  diagnostic: 'Run diagnostics',
  enrichment: 'Show how to improve',
  performance: 'Build performance report',
};

const POLL_MS = 4000;
// Long enough for a slow stage, short enough that a lost run does not leave the
// section spinning for ever.
const MAX_POLL_MS = 8 * 60 * 1000;
// Survives a page change but not the tab, so a stale marker cannot outlive the
// session and put a section into a state nothing is driving.
const RESUME_KEY = TARA_FEEDBACK_IN_FLIGHT_KEY;

const EMPTY = { phase: 'idle', data: null, status_label: null, error: null, startedAt: null };

export default function TaraWritingFeedbackPanel({
  attemptId, questionDbId, onToast, unlocked = true, onRequestUpgrade,
}) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [stages, setStages] = useState(() => ({
    diagnostic: { ...EMPTY }, enrichment: { ...EMPTY }, performance: { ...EMPTY },
  }));
  const [spans, setSpans] = useState({});
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const cancelled = useRef(false);
  const polling = useRef(null);

  const read = useCallback(async () => {
    const payload = await quizGeneratorAPI.getTaraWritingFeedback(attemptId, questionDbId);
    const next = taraFeedbackFromAttempt(payload);
    next.warnings.forEach((w) => console.warn(`TARA feedback: ${w}`));
    return next;
  }, [attemptId, questionDbId]);

  // Mount: rebuild whatever exists. One request, no writes.
  useEffect(() => {
    cancelled.current = false;
    if (!attemptId || !unlocked) { setLoading(false); return undefined; }

    (async () => {
      try {
        const next = await read();
        if (cancelled.current) return;
        setStages(next.stages);
        setSpans(next.spans);
        resumeInFlight(userId, next.stages, setStages);
      } catch (err) {
        if (cancelled.current) return;
        const copy = taraFeedbackErrorCopy(err);
        if (copy) {
          setStages((s) => ({ ...s, diagnostic: { ...s.diagnostic, phase: 'error', error: copy } }));
        }
      } finally {
        if (!cancelled.current) setLoading(false);
      }
    })();

    return () => { cancelled.current = true; };
  }, [attemptId, unlocked, read, userId]);

  const anyRunning = TARA_FEEDBACK_STAGES.some((s) => stages[s].phase === 'running');

  // Advance the progress copy while something is running, so a minute-long wait
  // does not look frozen.
  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(id);
  }, [anyRunning]);

  // Poll only while something is running.
  useEffect(() => {
    if (!anyRunning) {
      if (polling.current) { clearInterval(polling.current); polling.current = null; }
      return undefined;
    }
    const startedAt = Date.now();
    polling.current = setInterval(async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        clearInterval(polling.current);
        polling.current = null;
        return;
      }
      try {
        const next = await read();
        if (cancelled.current) return;
        setStages(next.stages);
        setSpans(next.spans);
      } catch (err) {
        // A failed poll is not a failed stage. Keep polling; the deadline ends it.
        console.warn('TARA feedback poll failed', err);
      }
    }, POLL_MS);
    return () => {
      if (polling.current) { clearInterval(polling.current); polling.current = null; }
    };
  }, [anyRunning, read]);

  const run = useCallback(async (stage) => {
    setStages((s) => ({
      ...s,
      // Kept only until the next poll returns the server's own start time.
      [stage]: { ...s[stage], phase: 'running', error: null, startedAt: Date.now() },
    }));
    markInFlight(userId, attemptId, stage);

    try {
      // Read before starting, so a stage that finished after a client timeout is
      // picked up rather than run again.
      const existing = await read();
      if (existing.stages[stage].phase === 'ready') {
        if (cancelled.current) return;
        setStages(existing.stages);
        setSpans(existing.spans);
        clearInFlight(userId);
        return;
      }

      await quizGeneratorAPI.runTaraWritingStage(attemptId, stage, questionDbId);
      const next = await read();
      if (cancelled.current) return;
      setStages(next.stages);
      setSpans(next.spans);
      if (next.stages[stage].phase !== 'running') clearInFlight(userId);
    } catch (err) {
      clearInFlight(userId);
      if (cancelled.current) return;
      const copy = taraFeedbackErrorCopy(err);
      if (!copy) return;
      if (!copy.retryable && onToast) onToast(copy.message);
      setStages((s) => ({ ...s, [stage]: { ...s[stage], phase: 'error', error: copy } }));
    }
  }, [attemptId, questionDbId, read, onToast, userId]);

  if (!unlocked) {
    return (
      <div className="tara-fb-stack">
        {TARA_FEEDBACK_STAGES.map((stage) => (
          <TaraFeedbackSection
            key={stage}
            title={TARA_STAGE_TITLES[stage]}
            phase="locked"
            lockReason="Writing task feedback comes with this paper's plan."
          />
        ))}
        <button
          type="button"
          className="tara-ws-start-btn"
          onClick={() => onRequestUpgrade?.()}
        >
          See plans
        </button>
      </div>
    );
  }

  if (loading) {
    return <p className="tara-fb-loading">Loading your feedback…</p>;
  }

  const gating = taraFeedbackGating(stages);
  const renderers = {
    diagnostic: TaraDiagnosticFeedback,
    enrichment: TaraEnrichmentFeedback,
    performance: TaraPerformanceReport,
  };

  return (
    <div className="tara-fb-stack">
      {TARA_FEEDBACK_STAGES.map((stage) => {
        const entry = stages[stage];
        const { phase, lockReason } = gating[stage];
        const Renderer = renderers[stage];
        return (
          <TaraFeedbackSection
            key={stage}
            title={TARA_STAGE_TITLES[stage]}
            phase={phase}
            statusLabel={entry.status_label}
            lockReason={lockReason}
            progress={taraFeedbackProgressCopy(stage, now - (entry.startedAt || now))}
            error={entry.error}
            runLabel={RUN_LABELS[stage]}
            onRun={() => run(stage)}
            onRetry={() => run(stage)}
          >
            <Renderer feedback={entry.data} spans={spans} />
          </TaraFeedbackSection>
        );
      })}
    </div>
  );
}

// The marker is the signed-in user's (sessionStorage, under their id): a
// student signing in after another in the same tab never sees theirs.
function markInFlight(userId, attemptId, stage) {
  try {
    writeUserSession(RESUME_KEY, userId, { attemptId, stage, startedAt: Date.now() });
  } catch (e) { /* private mode */ }
}

function clearInFlight(userId) {
  try { removeUserSession(RESUME_KEY, userId); } catch (e) { /* private mode */ }
}

// Leaving mid-generation and coming back should show the section still running,
// not idle. An idle section invites a second click and a second charge.
function resumeInFlight(userId, stages, setStages) {
  const marker = readUserSession(RESUME_KEY, userId);
  if (!marker || !marker.stage) return;
  if (Date.now() - (marker.startedAt || 0) > 3 * 60 * 1000) { clearInFlight(userId); return; }
  const entry = stages[marker.stage];
  if (!entry || entry.phase !== 'idle') { clearInFlight(userId); return; }
  setStages((s) => ({
    ...s,
    [marker.stage]: { ...s[marker.stage], phase: 'running', startedAt: marker.startedAt },
  }));
}
